import { useState, useRef, useEffect, useCallback } from 'react'
import type { Tasting, Taster } from '../../types'
import { indexToLetter } from '../../utils/scoring'
import { ScoreCell } from './ScoreCell'

type ScoreGridProps = {
  tasting: Tasting
  onAddTaster: (name: string) => void
  onUpdateTaster: (_key: string, updates: Partial<Pick<Taster, 'name' | 'scores'>>) => void
  onRemoveTaster: (_key: string) => void
}

export function ScoreGrid({
  tasting,
  onAddTaster,
  onUpdateTaster,
  onRemoveTaster,
}: ScoreGridProps) {
  const [newTasterName, setNewTasterName] = useState('')
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const prevTasterCount = useRef(tasting.tasters.length)
  const cellElements = useRef(new Map<string, HTMLInputElement>())

  const setCellRef = useCallback((tasterIdx: number, wineIdx: number, el: HTMLInputElement | null) => {
    const key = `${tasterIdx}-${wineIdx}`
    if (el) {
      cellElements.current.set(key, el)
    } else {
      cellElements.current.delete(key)
    }
  }, [])

  const focusCell = useCallback((tasterIdx: number, wineIdx: number) => {
    cellElements.current.get(`${tasterIdx}-${wineIdx}`)?.focus()
  }, [])

  useEffect(() => {
    if (tasting.tasters.length > prevTasterCount.current) {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
    }
    prevTasterCount.current = tasting.tasters.length
  }, [tasting.tasters.length])

  const wineCount = tasting.wines.length

  const isDuplicateName = (name: string, excludeKey?: string): boolean => {
    const normalized = name.trim().toLowerCase()
    return tasting.tasters.some((t) => t._key !== excludeKey && t.name.toLowerCase() === normalized)
  }

  const handleNewTasterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      const trimmed = newTasterName.trim()
      if (trimmed && !isDuplicateName(trimmed)) {
        onAddTaster(trimmed)
        setNewTasterName('')
      }
    }
  }

  const handleNewTasterBlur = () => {
    const trimmed = newTasterName.trim()
    if (trimmed && !isDuplicateName(trimmed)) {
      onAddTaster(trimmed)
      setNewTasterName('')
    }
  }

  const handleNameChange = (taster: Taster, newName: string) => {
    onUpdateTaster(taster._key, { name: newName })
  }

  const handleScoreChange = (taster: Taster, wineIndex: number, value: number | null) => {
    const newScores = [...taster.scores]
    newScores[wineIndex] = value
    onUpdateTaster(taster._key, { scores: newScores })
  }

  const getDuplicateScores = (scores: (number | null)[]): Set<number> => {
    const seen = new Set<number>()
    const duplicates = new Set<number>()
    for (const score of scores) {
      if (score !== null) {
        if (seen.has(score)) {
          duplicates.add(score)
        }
        seen.add(score)
      }
    }
    return duplicates
  }

  const handleDeleteClick = (taster: Taster) => {
    setDeleteConfirmId(taster._key)
  }

  const handleDeleteConfirm = (id: string) => {
    onRemoveTaster(id)
    setDeleteConfirmId(null)
  }

  const isTasterIncomplete = (taster: Taster): boolean => {
    return taster.scores.some((s) => s === null)
  }

  return (
    <div className="overflow-x-auto py-4">
      <table className="border-collapse mx-auto">
        <thead>
          <tr>
            <th className="sticky top-0 left-0 z-20 bg-[#ddd8c4] p-2 md:p-3 min-w-[150px] md:min-w-[200px] text-left font-bold text-base md:text-lg">
              Taster
            </th>
            {tasting.wines.map((_, index) => (
              <th
                key={index}
                className="sticky top-0 z-10 bg-[#ddd8c4] p-2 md:p-3 text-center font-bold text-base md:text-lg min-w-[60px] md:min-w-[80px]"
              >
                {indexToLetter(index)}
              </th>
            ))}
            <th className="sticky top-0 z-10 bg-[#ddd8c4] p-2 md:p-3 w-10 md:w-14"></th>
          </tr>
        </thead>
        <tbody>
          {tasting.tasters.map((taster) => {
            const duplicates = getDuplicateScores(taster.scores)
            const nameIsDuplicate = isDuplicateName(taster.name, taster._key)
            const isIncomplete = isTasterIncomplete(taster)

            return (
              <tr key={taster._key} className={isIncomplete ? 'bg-orange-50' : ''}>
                <td className="sticky left-0 z-10 bg-cream p-2 md:p-3">
                  <input
                    type="text"
                    value={taster.name}
                    onChange={(e) => handleNameChange(taster, e.target.value)}
                    className={`w-full px-2 py-2 md:px-3 md:py-3 text-base md:text-lg border rounded bg-white focus:outline-none focus:border-black ${
                      nameIsDuplicate ? 'border-red-500 bg-red-100' : 'border-black/20'
                    }`}
                  />
                </td>
                {taster.scores.map((score, wineIndex) => {
                  const tasterIdx = tasting.tasters.indexOf(taster)
                  return (
                    <td key={wineIndex} className="p-2 md:p-3">
                      <ScoreCell
                        value={score}
                        maxValue={wineCount}
                        onChange={(value) => handleScoreChange(taster, wineIndex, value)}
                        hasError={score !== null && duplicates.has(score)}
                        isEmpty={score === null}
                        inputRef={(el) => setCellRef(tasterIdx, wineIndex, el)}
                        onAdvance={(num) => {
                          const isDuplicate = taster.scores.some((s, i) => i !== wineIndex && s === num)
                          if (!isDuplicate && wineIndex < wineCount - 1) {
                            focusCell(tasterIdx, wineIndex + 1)
                          }
                        }}
                        onRetreat={() => {
                          if (wineIndex > 0) {
                            focusCell(tasterIdx, wineIndex - 1)
                          }
                        }}
                      />
                    </td>
                  )
                })}
                <td className="p-2 md:p-3">
                  {deleteConfirmId === taster._key ? (
                    <div className="flex gap-1 md:gap-2">
                      <button
                        onClick={() => handleDeleteConfirm(taster._key)}
                        className="px-2 py-1 md:px-3 md:py-2 text-xs md:text-sm bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(null)}
                        className="px-2 py-1 md:px-3 md:py-2 text-xs md:text-sm bg-black/10 rounded hover:bg-black/20"
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleDeleteClick(taster)}
                      className="w-8 h-8 md:w-10 md:h-10 text-lg md:text-xl text-black/30 hover:text-black rounded hover:bg-black/10"
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
          <tr>
            <td className="sticky left-0 z-10 bg-cream p-2 md:p-3">
              <input
                type="text"
                value={newTasterName}
                onChange={(e) => setNewTasterName(e.target.value)}
                onKeyDown={handleNewTasterKeyDown}
                onBlur={handleNewTasterBlur}
                placeholder="Add taster..."
                className={`w-full px-2 py-2 md:px-3 md:py-3 text-base md:text-lg border rounded bg-white focus:outline-none focus:border-black ${
                  newTasterName && isDuplicateName(newTasterName)
                    ? 'border-red-500 bg-red-100'
                    : 'border-black/20'
                }`}
              />
            </td>
            {tasting.wines.map((_, index) => (
              <td key={index} className="p-2 md:p-3">
                <div className="w-12 h-12 md:w-16 md:h-16 border border-dashed border-black/20 rounded" />
              </td>
            ))}
            <td className="p-2 md:p-3 w-10 md:w-14"></td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}
